/**
 * SOLOMON CHAT INTERFACE
 * The primary interaction layer of Solomon.
 *
 * Day 4 upgrades:
 * - Mode tabs REMOVED — Solomon auto-detects the best mode per message
 * - Mode badge appears on each Solomon response showing what was used
 * - Users can switch mode from the badge on the last message
 *
 * Day 6 upgrades:
 * - isListening passed to orb for blue glow animation
 * - interimText shown in input area while speaking into mic
 *
 * 2026-07-28: the header icon row is gone. Both surfaces now carry only the
 * hamburger (the sidebar's sole entry point), the Capabilities strip, and
 * FrontFaceControls. Everything else is reached through the sidebar or the
 * Capabilities map — see components/Capabilities/ and capabilities/registry.js.
 *
 * Two states:
 * 1. Hero — Orb centered, no messages, clean greeting
 * 2. Active — Messages visible, orb small, full chat layout
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import MessageBubble, { ThinkingIndicator } from './MessageBubble';
import InputBar                             from './InputBar';
import { attachFiles as routeAttachFiles }  from './attachFiles';
import FrontFaceControls                    from './FrontFaceControls';
import ZodiacOrb                            from '../SolomonOrb/ZodiacOrb.jsx';
import { formatHeroTime }                   from '../../lib/heroClock.js';
import MemoryPanel                          from '../MemoryPanel';
import AmbientField                         from '../AmbientField';
import ConversationSidebar, { HamburgerIcon } from '../ConversationSidebar';
import SettingsPanel                           from '../SettingsPanel';
import ToolModal                               from '../VisualTools/ToolModal';
import SignalsPanel                            from '../SignalsPanel';
import { UndoBanner }                        from '../ActionCard';
import CapabilitiesStrip                       from '../Capabilities/CapabilitiesStrip';
import CapabilitiesPanel                       from '../Capabilities/CapabilitiesPanel';
import './chat.css';
import '../VisualTools/visualTools.css';
import '../Capabilities/capabilities.css';

// ============================================================
// NEURAL QUANTUM ICON — miniature node-network matching the orb
// Nodes + connections + faint electron orbital rings
// ============================================================

function NeuralQuantumIcon({ size = 32 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Colors via CSS custom properties (design tokens) — stop-color and
          stroke are CSS properties, so style={} resolves var() where the SVG
          presentation ATTRIBUTES cannot. Zero raw hex (Task 18). */}
      <defs>
        <radialGradient id="nq-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   style={{ stopColor: 'var(--solomon-orange-light)' }} />
          <stop offset="100%" style={{ stopColor: 'var(--solomon-gold)' }} />
        </radialGradient>
        <radialGradient id="nq-node" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   style={{ stopColor: 'var(--solomon-orange-lighter)' }} />
          <stop offset="100%" style={{ stopColor: 'var(--solomon-gold)' }} />
        </radialGradient>
        <linearGradient id="nq-line" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   style={{ stopColor: 'var(--solomon-gold-bright)' }} stopOpacity="0.6" />
          <stop offset="100%" style={{ stopColor: 'var(--solomon-gold)' }} stopOpacity="0.25" />
        </linearGradient>
      </defs>

      {/* ── Quantum orbital rings — faint ellipses at angles ── */}
      <ellipse cx="16" cy="16" rx="14" ry="5.5"
        style={{ stroke: 'var(--solomon-gold)' }} strokeWidth="0.55" strokeOpacity="0.22" fill="none"
        transform="rotate(30 16 16)" />
      <ellipse cx="16" cy="16" rx="14" ry="5.5"
        style={{ stroke: 'var(--solomon-gold)' }} strokeWidth="0.55" strokeOpacity="0.22" fill="none"
        transform="rotate(-30 16 16)" />
      <ellipse cx="16" cy="16" rx="14" ry="5.5"
        style={{ stroke: 'var(--solomon-gold)' }} strokeWidth="0.45" strokeOpacity="0.16" fill="none"
        transform="rotate(90 16 16)" />

      {/* ── Connection lines between nodes ── */}
      {/* center → outer ring */}
      <line x1="16" y1="16" x2="6"  y2="7"  stroke="url(#nq-line)" strokeWidth="0.8" />
      <line x1="16" y1="16" x2="26" y2="7"  stroke="url(#nq-line)" strokeWidth="0.8" />
      <line x1="16" y1="16" x2="3"  y2="16" stroke="url(#nq-line)" strokeWidth="0.8" />
      <line x1="16" y1="16" x2="29" y2="16" stroke="url(#nq-line)" strokeWidth="0.8" />
      <line x1="16" y1="16" x2="6"  y2="25" stroke="url(#nq-line)" strokeWidth="0.8" />
      <line x1="16" y1="16" x2="26" y2="25" stroke="url(#nq-line)" strokeWidth="0.8" />
      <line x1="16" y1="16" x2="16" y2="3"  stroke="url(#nq-line)" strokeWidth="0.8" />
      <line x1="16" y1="16" x2="16" y2="29" stroke="url(#nq-line)" strokeWidth="0.8" />
      {/* ring-to-ring cross connections */}
      <line x1="6"  y1="7"  x2="26" y2="7"  stroke="url(#nq-line)" strokeWidth="0.55" />
      <line x1="6"  y1="25" x2="26" y2="25" stroke="url(#nq-line)" strokeWidth="0.55" />
      <line x1="6"  y1="7"  x2="3"  y2="16" stroke="url(#nq-line)" strokeWidth="0.55" />
      <line x1="26" y1="7"  x2="29" y2="16" stroke="url(#nq-line)" strokeWidth="0.55" />
      <line x1="6"  y1="25" x2="3"  y2="16" stroke="url(#nq-line)" strokeWidth="0.55" />
      <line x1="26" y1="25" x2="29" y2="16" stroke="url(#nq-line)" strokeWidth="0.55" />
      {/* diagonal skip connections — adds neural-web density */}
      <line x1="6"  y1="7"  x2="26" y2="25" stroke="url(#nq-line)" strokeWidth="0.40" strokeOpacity="0.4" />
      <line x1="26" y1="7"  x2="6"  y2="25" stroke="url(#nq-line)" strokeWidth="0.40" strokeOpacity="0.4" />
      <line x1="16" y1="3"  x2="29" y2="16" stroke="url(#nq-line)" strokeWidth="0.40" strokeOpacity="0.35" />
      <line x1="16" y1="3"  x2="3"  y2="16" stroke="url(#nq-line)" strokeWidth="0.40" strokeOpacity="0.35" />
      <line x1="16" y1="29" x2="29" y2="16" stroke="url(#nq-line)" strokeWidth="0.40" strokeOpacity="0.35" />
      <line x1="16" y1="29" x2="3"  y2="16" stroke="url(#nq-line)" strokeWidth="0.40" strokeOpacity="0.35" />

      {/* ── Outer ring nodes ── */}
      <circle cx="6"  cy="7"  r="1.8" fill="url(#nq-node)" opacity="0.80" />
      <circle cx="26" cy="7"  r="1.8" fill="url(#nq-node)" opacity="0.80" />
      <circle cx="3"  cy="16" r="1.5" fill="url(#nq-node)" opacity="0.65" />
      <circle cx="29" cy="16" r="1.5" fill="url(#nq-node)" opacity="0.65" />
      <circle cx="6"  cy="25" r="1.8" fill="url(#nq-node)" opacity="0.80" />
      <circle cx="26" cy="25" r="1.8" fill="url(#nq-node)" opacity="0.80" />
      <circle cx="16" cy="3"  r="1.4" fill="url(#nq-node)" opacity="0.60" />
      <circle cx="16" cy="29" r="1.4" fill="url(#nq-node)" opacity="0.60" />

      {/* ── Central nucleus — brightest, with glow ── */}
      <circle cx="16" cy="16" r="6.5" fill="url(#nq-core)" opacity="0.12" />
      <circle cx="16" cy="16" r="3.8" fill="url(#nq-core)" opacity="0.20" />
      <circle cx="16" cy="16" r="2.6" fill="url(#nq-core)" opacity="0.95" />
    </svg>
  );
}


// ============================================================
// HEADER (active state)
// ============================================================

function ChatHeader({
  onNewChat,
  onLogout,
  onToggleSidebar,
  // Capability surface
  capabilityState,
  onOpenCapabilities,
  // Voice panel props
  voiceEnabled,
  selectedVoiceId,
  isSpeaking,
  fetchVoices,
  onToggleVoice,
  onSelectVoice,
  onStopSpeaking,
}) {
  return (
    <div className="chat-header">
      {/* Left: hamburger (the only route to the sidebar, which holds
          Capabilities, Settings and conversation history) + the capability
          strip. The three workspace toggles that used to sit here are reached
          through the Capabilities map now. */}
      <div className="chat-header-left">
        <button
          className="sidebar-toggle-btn"
          onClick={onToggleSidebar}
          title="Conversation history"
          aria-label="Open conversation history"
        >
          <HamburgerIcon />
        </button>
        {/* The hero is destroyed by the first auto-briefing of each day, so a
            hero-only strip would be least visible exactly when it matters. */}
        <div className="chat-header-cap">
          <CapabilitiesStrip derived={capabilityState} onOpen={onOpenCapabilities} />
        </div>
      </div>

      {/* Centered branding */}
      <div className="chat-header-center">
        <span className="chat-header-title">Solomon</span>
      </div>

      <div className="chat-header-right">
        <FrontFaceControls
          voiceEnabled={voiceEnabled}
          selectedVoiceId={selectedVoiceId}
          isSpeaking={isSpeaking}
          fetchVoices={fetchVoices}
          onToggleVoice={onToggleVoice}
          onSelectVoice={onSelectVoice}
          onStopSpeaking={onStopSpeaking}
          onNewChat={onNewChat}
          onLogout={onLogout}
        />
      </div>
    </div>
  );
}

// ============================================================
// MESSAGE LIST
// ============================================================

function MessageList({ messages, isThinking, thinkingModel, isLoadingHistory, onRegenerateWithMode, onEditMessage, pendingActions = [], onApproveAction, onCancelAction }) {
  const bottomRef = useRef(null);
  const listRef = useRef(null);

  // Auto-scroll to bottom as content arrives — but NOT during a history load
  // (loaded messages should appear at the top of the new view).
  //
  // This effect re-fires on EVERY streamed chunk (each chunk mutates `messages`).
  // Two rules keep that from turning into the "page jumps up and down" symptom:
  //   1. behavior:'auto' (instant), never 'smooth'. A smooth scroll launches an
  //      animation per chunk; with many chunks/sec those stack and cancel each
  //      other → the visible bounce. Instant scroll just re-pins to the bottom.
  //   2. Only follow if the user is already near the bottom (~120px). If they've
  //      scrolled up to read, mid-stream chunks won't yank them back down.
  useEffect(() => {
    if (isLoadingHistory) return;
    const el = listRef.current;
    if (el) {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom > 120) return; // user scrolled up — don't hijack
    }
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages, isThinking, isLoadingHistory]);

  // Find the index of the last assistant message (for showing switch button)
  let lastAssistantIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') { lastAssistantIdx = i; break; }
  }

  // Pill only fills the gap: once the streaming placeholder bubble exists
  // (added at sendMessage start), it — not this separate pill — carries the
  // thinking bar/step lines. Rendering both was the two-progress-surfaces
  // bug this merge fixes.
  const lastMessage = messages[messages.length - 1];
  const hasStreamingCard = !!(lastMessage && lastMessage.role === 'assistant' && lastMessage.isStreaming === true);

  return (
    <div className="message-list" ref={listRef} role="log" aria-live="polite" aria-label="Conversation">
      <div className="message-list-inner">
        {messages.map((msg, idx) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            onRegenerateWithMode={onRegenerateWithMode}
            onEditMessage={onEditMessage}
            isLastMessage={idx === lastAssistantIdx}
            pendingActions={pendingActions}
            onApproveAction={onApproveAction}
            onCancelAction={onCancelAction}
          />
        ))}
        {isThinking && !hasStreamingCard && <ThinkingIndicator modelHint={thinkingModel} />}
        <div ref={bottomRef} />
      </div>

      {isLoadingHistory && (
        <div className="message-list-loading" role="status" aria-live="polite">
          <div className="message-list-loading-pill">
            <span className="message-list-loading-dot" />
            <span className="message-list-loading-dot" />
            <span className="message-list-loading-dot" />
            <span className="message-list-loading-text">Loading conversation…</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TIME-AWARE GREETING
// ============================================================
// Windows: morning 5–11:59am, afternoon 12–5:59pm, evening 6pm–4:59am.
// Local time — matches user's perception, not server UTC.
function getTimeGreeting(name) {
  const hour = new Date().getHours();
  const period = hour >= 5 && hour < 12  ? 'morning'
               : hour >= 12 && hour < 18 ? 'afternoon'
               : 'evening';
  return `Good ${period}, ${name}`;
}

// Scoped subcomponent — isolates 60s tick so parent ChatInterface
// doesn't re-render every minute.
// Exported (additively) so the dev preview harness renders the REAL greeting —
// including the host-local Arabic-Indic clock — instead of a replica that
// silently drifts. Same reason CapabilitiesStrip and FrontFaceControls are
// imported there from src/ rather than hand-copied.
export function HeroGreeting({ userName }) {
  const [timeStr, setTimeStr] = useState(() => formatHeroTime());
  useEffect(() => {
    const id = setInterval(() => setTimeStr(formatHeroTime()), 60_000);
    return () => clearInterval(id);
  }, []);
  return (
    <h1 className="chat-hero-greeting">
      {getTimeGreeting(userName)} —{' '}
      {/* The clock never splits across lines: on a 390px phone the wrap landed
          between the digits and "PM". The line breaks before it instead. */}
      <span className="chat-hero-clock">{timeStr}</span>
    </h1>
  );
}

// ============================================================
// MAIN CHAT INTERFACE
// ============================================================

export default function ChatInterface({
  messages             = [],
  isThinking           = false,
  thinkingModel        = null,
  isSpeaking           = false,
  isListening          = false,
  isError              = false,
  isLoadingHistory     = false,
  interimText          = '',
  theme                = 'dark',
  // Voice panel
  voiceEnabled         = false,
  selectedVoiceId      = '',
  fetchVoices,
  onToggleVoice,
  onSelectVoice,
  onStopSpeaking,
  sttIsSupported       = false,
  // Sidebar
  sidebarOpen          = false,
  onToggleSidebar,
  onSelectConversation,
  activeConversationId = null,
  // Settings
  settingsOpen         = false,
  settingsSection      = null,
  onToggleSettings,
  // Distinct from onToggleSettings on purpose: the sidebar entry must OPEN, not
  // flip. Bound to the toggle it was the same hazard `openWorkspace` exists to
  // prevent — unreachable today only because the settings backdrop covers the
  // sidebar, i.e. an accident, not a design.
  onOpenSettings,
  // Unified tool modal state — null | 'compose' | 'signals' | ...
  activeTool           = null,
  onSetActiveTool,
  // Capability surface — derived state is owned by the single hook in App.
  // Teach Solomon, Memory, Signals and the three workspaces are all reached
  // through this map now; ChatInterface no longer threads a prop per entry.
  capabilityState,
  onOpenCapabilities,
  capabilitiesOpen     = false,
  onCloseCapabilities,
  onInvokeCapability,
  // Integrations
  gmailConnected       = false,
  onConnectGmail,
  onDisconnectGmail,
  // Other handlers
  onSend,
  onStop,
  onVoiceToggle,
  onFileUpload,
  uploadProgress       = null,
  onCancelUpload,
  // Ephemeral chat attachments (sticky-for-conversation)
  onDocumentAttach,
  attachments          = [],
  onClearAttachment,
  onClearAllAttachments,
  // Pending images (ephemeral one-turn vision attachments)
  onImageAttach,
  pendingImages        = [],
  onClearPendingImage,
  onNewChat,
  onThemeToggle,
  onRegenerateWithMode,
  // Task 3: edit & resend a prior user message
  onEditMessage,
  pendingDraft         = null,
  onDraftConsumed,
  onLogout,
  // Action guardrails (Phase 2A)
  pendingActions       = [],
  activeUndoBanner     = null,
  onApproveAction,
  onCancelAction,
  onUndoAction,
  onDismissUndoBanner,
  // User identity (for hero greeting)
  userName             = 'there',
}) {
  const chatActive = messages.length > 0;

  // Stable, deliberately. ToolModal registers an ESC layer keyed to `onClose`
  // in its effect deps — an inline arrow makes that effect re-run on EVERY
  // parent render (each streamed token, each drag-state flip), popping and
  // re-pushing ToolModal to the top of the layer stack. Nothing inverts today,
  // but "topmost wins" would depend on render frequency rather than open
  // order — a latent version of the exact bug layers.js exists to fix.
  const closeActiveTool = useCallback(() => onSetActiveTool(null), [onSetActiveTool]);

  // ── Full chat-area drag-and-drop file attach ──
  // A counter ref tracks dragenter/dragleave so child elements crossing the
  // boundary don't flicker the overlay. The overlay only renders when the
  // counter is > 0 AND the drag actually carries files. Drops route through
  // the SAME shared helper as the input bar (doc/audio split + one-doc guard).
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  const [dragError, setDragError] = useState(null);

  useEffect(() => {
    if (!dragError) return;
    const t = setTimeout(() => setDragError(null), 4000);
    return () => clearTimeout(t);
  }, [dragError]);

  const dragHasFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes('Files');

  const handleAreaDragEnter = useCallback((e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragActive(true);
  }, []);

  const handleAreaDragOver = useCallback((e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();  // required to allow the drop
  }, []);

  const handleAreaDragLeave = useCallback((e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragActive(false);
    }
  }, []);

  const handleAreaDrop = useCallback((e) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    routeAttachFiles(e.dataTransfer?.files, {
      onImageAttach,
      onDocumentAttach,
      onFileUpload,
      onError: setDragError,
    });
  }, [onImageAttach, onDocumentAttach, onFileUpload]);

  return (
    <div
      className="chat-layout"
      onDragEnter={handleAreaDragEnter}
      onDragOver={handleAreaDragOver}
      onDragLeave={handleAreaDragLeave}
      onDrop={handleAreaDrop}
    >

      {/* Full chat-area drag-and-drop overlay — only mounted while dragging
          files, so it never blocks clicks at rest. */}
      {dragActive && (
        <div className="chat-drop-overlay" aria-hidden="true">
          <div className="chat-drop-overlay-inner">
            <UploadCloudIcon />
            <span className="chat-drop-overlay-title">Drop a file to attach</span>
            <span className="chat-drop-overlay-sub">PDF, Word, CSV, TXT, Markdown</span>
          </div>
        </div>
      )}

      {/* Transient drop error (unsupported type / too large / multi-doc) */}
      {dragError && (
        <div className="chat-drop-error" role="status">{dragError}</div>
      )}

      {/* Conversation history sidebar */}
      <ConversationSidebar
        isOpen={sidebarOpen}
        onToggle={onToggleSidebar}
        onSelectConversation={onSelectConversation}
        activeConversationId={activeConversationId}
        onNewChat={onNewChat}
        onOpenCapabilities={onOpenCapabilities}
        onOpenSettings={onOpenSettings}
      />

      {/* Capability map — sibling of the workspaces so it is reachable from
          both the hero strip and the sidebar, in either chat state. */}
      <CapabilitiesPanel
        open={capabilitiesOpen}
        derived={capabilityState}
        onClose={onCloseCapabilities}
        onInvoke={onInvokeCapability}
      />

      {/* Settings panel */}
      <SettingsPanel
        isOpen={settingsOpen}
        initialSection={settingsSection}
        onClose={onToggleSettings}
        voiceEnabled={voiceEnabled}
        selectedVoiceId={selectedVoiceId}
        onToggleVoice={onToggleVoice}
        onSelectVoice={onSelectVoice}
        fetchVoices={fetchVoices}
        theme={theme}
        onThemeToggle={onThemeToggle}
        gmailConnected={gmailConnected}
        onConnectGmail={onConnectGmail}
        onDisconnectGmail={onDisconnectGmail}
      />

      {/* Unified tool modals — single activeTool state drives which renders.
          The Google write-tools (compose / calendar-event / sheet-row /
          task-create / view-tasks) were removed 2026-07-28 per D9 Option B —
          see docs/superpowers/specs/2026-07-27-capability-surface-design.md.
          Backend services and routes are untouched; only these UI surfaces went. */}
      {activeTool === 'signals' && (
        <ToolModal title="Signals" onClose={closeActiveTool}>
          <SignalsPanel />
        </ToolModal>
      )}
      {/* Memory owns its own ToolModal and returns null when closed, so its
          30s status poll stops with it. Teach Solomon is the deliberate
          opposite — mounted-hidden in App.jsx. Do not unify them. */}
      <MemoryPanel
        isOpen={activeTool === 'memory'}
        onClose={closeActiveTool}
      />
      {/* Call Queue / Find Leads / Leads (+import) relocated to the Wholesale
          workspace (panel beside Research). Lead deep-links now open that panel
          on the Leads section via App.jsx. */}

      {/* Quantum particle field — always present, dimmed in chat for readability */}
      <AmbientField isSpeaking={isSpeaking} isThinking={isThinking} dimmed={chatActive} />

      {/* ---- HERO STATE ---- */}
      {!chatActive && (
        <div className="chat-hero-state">
          {/* Top bar — hamburger on the left, front-face controls on the
              right. Same control set as the active-chat header. */}
          <div className="chat-hero-topbar">
            <div className="chat-hero-topbar-left">
              <button
                className="sidebar-toggle-btn"
                onClick={onToggleSidebar}
                title="Conversation history"
                aria-label="Open conversation history"
              >
                <HamburgerIcon />
              </button>
            </div>
            <div className="chat-hero-topbar-right">
              <FrontFaceControls
                voiceEnabled={voiceEnabled}
                selectedVoiceId={selectedVoiceId}
                isSpeaking={isSpeaking}
                fetchVoices={fetchVoices}
                onToggleVoice={onToggleVoice}
                onSelectVoice={onSelectVoice}
                onStopSpeaking={onStopSpeaking}
                onNewChat={onNewChat}
                onLogout={onLogout}
              />
            </div>
          </div>

          {/* Hero orb — the astrolabe ring (calendar) around the lattice core (state) */}
          <div className="chat-hero-content">
            <ZodiacOrb
              isSpeaking={isSpeaking}
              isThinking={isThinking}
              isListening={isListening}
              isError={isError}
              onOrbClick={() => {}}
              theme={theme}
            />
          </div>

          {/* Time-aware greeting — single focal text element between orb + input */}
          <HeroGreeting userName={userName} />

          {/* Capability surface entry point */}
          <CapabilitiesStrip derived={capabilityState} onOpen={onOpenCapabilities} />

          {/* Input bar in hero */}
          <div className="chat-hero-input">
            <div className="input-bar-inner">
              <InputBar
                onSend={onSend}
                onStop={onStop}
                onVoiceToggle={onVoiceToggle}
                onFileUpload={onFileUpload}
                onCancelUpload={onCancelUpload}
                uploadProgress={uploadProgress}
                onDocumentAttach={onDocumentAttach}
                attachments={attachments}
                onClearAttachment={onClearAttachment}
                onClearAllAttachments={onClearAllAttachments}
                onImageAttach={onImageAttach}
                pendingImages={pendingImages}
                onClearPendingImage={onClearPendingImage}
                isListening={isListening}
                isThinking={isThinking}
                interimText={interimText}
                sttIsSupported={sttIsSupported}
                pendingDraft={pendingDraft}
                onDraftConsumed={onDraftConsumed}
              />
            </div>
          </div>
        </div>
      )}

      {/* ---- ACTIVE CHAT STATE ---- */}
      {chatActive && (
        <div className="chat-active-state">

          {/* Header with voice controls */}
          <ChatHeader
            onNewChat={onNewChat}
            onLogout={onLogout}
            onToggleSidebar={onToggleSidebar}
            capabilityState={capabilityState}
            onOpenCapabilities={onOpenCapabilities}
            voiceEnabled={voiceEnabled}
            selectedVoiceId={selectedVoiceId}
            isSpeaking={isSpeaking}
            fetchVoices={fetchVoices}
            onToggleVoice={onToggleVoice}
            onSelectVoice={onSelectVoice}
            onStopSpeaking={onStopSpeaking}
          />

          {/* Undo banner — shown after action approval */}
          {activeUndoBanner && (
            <UndoBanner
              actionId={activeUndoBanner.actionId}
              actionType={activeUndoBanner.actionType}
              reversible={activeUndoBanner.reversible}
              undoDeadline={activeUndoBanner.undoDeadline}
              onUndo={onUndoAction}
              onDismiss={onDismissUndoBanner}
            />
          )}

          {/* Messages */}
          <MessageList
            messages={messages}
            isThinking={isThinking}
            thinkingModel={thinkingModel}
            isLoadingHistory={isLoadingHistory}
            onRegenerateWithMode={onRegenerateWithMode}
            onEditMessage={onEditMessage}
            pendingActions={pendingActions}
            onApproveAction={onApproveAction}
            onCancelAction={onCancelAction}
          />

          {/* Input bar */}
          <div className="input-bar-wrapper">
            <div className="input-bar-inner">
              <InputBar
                onSend={onSend}
                onStop={onStop}
                onVoiceToggle={onVoiceToggle}
                onFileUpload={onFileUpload}
                onCancelUpload={onCancelUpload}
                uploadProgress={uploadProgress}
                onDocumentAttach={onDocumentAttach}
                attachments={attachments}
                onClearAttachment={onClearAttachment}
                onClearAllAttachments={onClearAllAttachments}
                onImageAttach={onImageAttach}
                pendingImages={pendingImages}
                onClearPendingImage={onClearPendingImage}
                isListening={isListening}
                isThinking={isThinking}
                interimText={interimText}
                sttIsSupported={sttIsSupported}
                pendingDraft={pendingDraft}
                onDraftConsumed={onDraftConsumed}
              />
              <p className="input-hint">⏎ to send  ·  Shift+⏎ for new line</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ICONS
// ============================================================

function UploadCloudIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 16l-4-4-4 4" />
      <path d="M12 12v9" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
  );
}
